<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

/**
 * Names the person and the address an outbound message is about to reach.
 *
 * This is the minimal slice of the "verify identity, destination and consent
 * before sending" capability: it makes the destination visible, which is the
 * part that costs nothing and catches the common mistakes (replying in the
 * wrong tab, a contact whose number changed). It deliberately does NOT gate
 * sending, resolve identity or check consent. Those need a canonical identity
 * record and must not be faked by inference, so nothing here may be presented
 * as verified.
 */
const props = defineProps({
  contact: {
    type: Object,
    default: () => ({}),
  },
  channelName: {
    type: String,
    default: '',
  },
});

const { t } = useI18n();

const destination = computed(() => {
  const { phone_number: phoneNumber, email, identifier } = props.contact || {};
  return phoneNumber || email || identifier || '';
});

const name = computed(
  () => props.contact?.name || t('CONVERSATION.REPLYBOX.RECIPIENT_UNKNOWN')
);
</script>

<template>
  <div
    class="flex items-center gap-1.5 px-4 py-1 text-xs border-b text-n-slate-11 border-n-weak"
  >
    <fluent-icon icon="arrow-right" size="12" class="flex-shrink-0" />
    <span class="truncate">
      {{ t('CONVERSATION.REPLYBOX.SENDING_TO', { name: name }) }}
    </span>
    <span v-if="destination" class="font-medium truncate text-n-slate-12">
      {{ destination }}
    </span>
    <span v-if="channelName" class="flex-shrink-0 text-n-slate-10">
      {{ `· ${channelName}` }}
    </span>
  </div>
</template>
